import { StyleSheet, css } from 'aphrodite';
import { type FC, type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import type { CoachMessage } from '../ai';
import { IconX } from './icons';

/**
 * A single message pinned to the right rail.
 *
 * The left border is colored by the triggering keyword pin (`msg.pinColor`)
 * or falls back to `--accent` for user-pinned coaching messages. A small mono
 * eyebrow surfaces the triggering keyword (uppercased) or "Pinned" when none
 * is set.
 *
 * Body preference order:
 *   1. `msg.sayThis` — the parsed "Say this:" suggestion from the coaching response.
 *   2. `msg.text` — the raw AI text.
 *
 * When `maxHeight` is set, the body becomes vertically scrollable and a drag
 * handle at the bottom edge lets the user resize the card. The handle mirrors
 * the AppDrawer's resize-handle pattern (mouse events + clamped delta) and
 * commits the new height to the parent on mouseup.
 *
 * When `elevated` is true (used for the sticky-top GAME PLAN card in
 * `CenterColumn`) the card gets a thicker left border + a stronger shadow so
 * it visually outranks the ad-hoc pinned cards in the scrollable stack.
 */
type PinnedCardProps = {
	msg: CoachMessage;
	onUnpin: () => void;
	maxHeight?: number;
	onResize?: (height: number) => void;
	/**
	 * When true, render with elevated styling: thicker left border, stronger
	 * shadow, slightly larger eyebrow. Used by the sticky-top GAME PLAN card
	 * so it visually outranks ad-hoc pinned cards. See I5.
	 */
	elevated?: boolean;
};

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 700;
const DEFAULT_HEIGHT = 260;

export const PinnedCard: FC<PinnedCardProps> = ({ msg, onUnpin, maxHeight, onResize, elevated }) => {
	const color = msg.pinColor || 'var(--accent)';
	const eyebrow = (msg.pinnedKeyword || 'Pinned').toUpperCase();
	const body = msg.sayThis?.trim() || msg.text || '';

	const cardRef = useRef<HTMLDivElement>(null);
	const startYRef = useRef(0);
	const startHeightRef = useRef(0);
	const [isDragging, setIsDragging] = useState(false);

	const onHandleMouseDown = (e: ReactMouseEvent) => {
		if (!onResize) return;
		e.preventDefault();
		startYRef.current = e.clientY;
		startHeightRef.current = maxHeight ?? cardRef.current?.offsetHeight ?? DEFAULT_HEIGHT;
		setIsDragging(true);
	};

	useEffect(() => {
		if (!isDragging || !onResize) return;

		const handleMove = (e: MouseEvent) => {
			const delta = e.clientY - startYRef.current;
			const next = Math.min(Math.max(startHeightRef.current + delta, MIN_HEIGHT), MAX_HEIGHT);
			onResize(next);
		};
		const handleUp = () => setIsDragging(false);

		document.addEventListener('mousemove', handleMove);
		document.addEventListener('mouseup', handleUp);
		document.body.style.cursor = 'row-resize';
		document.body.style.userSelect = 'none';
		return () => {
			document.removeEventListener('mousemove', handleMove);
			document.removeEventListener('mouseup', handleUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [isDragging, onResize]);

	// Always give the card an explicit height so the body's `flex: 1` +
	// `overflowY: auto` has a height to flex against. Without this, long
	// rich-text Markdown content can blow the card open and overlap the
	// next card in the stack (the resize handle's `position: absolute;
	// bottom: 0` rides along with the grown height). When the user has
	// resized via localStorage we honor that value; otherwise we fall
	// back to DEFAULT_HEIGHT.
	//
	// `elevated` borrows the inline-style channel for `borderLeftWidth` +
	// `boxShadow` so they ride alongside the dynamic `borderLeftColor`
	// without fighting Aphrodite specificity.
	const cardStyle: React.CSSProperties = {
		borderLeftColor: color,
		height: maxHeight ?? DEFAULT_HEIGHT,
		...(elevated
			? {
					borderLeftWidth: 5,
					boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
				}
			: {}),
	};

	return (
		<div ref={cardRef} className={css(styles.card)} style={cardStyle} data-elevated={elevated ? 'true' : 'false'}>
			<div className={css(styles.header)}>
				<span className={css(styles.eyebrow, elevated && styles.eyebrowElevated)} style={{ color }}>
					{eyebrow}
				</span>
				<button type='button' title='Unpin' onClick={onUnpin} className={css(styles.closeBtn)}>
					<IconX size={12} />
				</button>
			</div>
			<div className={css(styles.body)}>
				<AIMarkdownResponse>{body}</AIMarkdownResponse>
			</div>
			{msg.time && <div className={css(styles.time)}>{msg.time}</div>}
			{onResize && (
				<div
					className={css(styles.resizeHandle, isDragging && styles.resizeHandleDragging)}
					onMouseDown={onHandleMouseDown}
					onDoubleClick={() => onResize(0)}
					role='separator'
					aria-orientation='horizontal'
					aria-label='Resize pinned message'
					title='Drag to resize · double-click to reset'
				/>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	card: {
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		background: 'var(--bg-elev)',
		borderRadius: 10,
		border: '1px solid var(--border)',
		borderLeft: '3px solid var(--accent)',
		padding: '12px 14px',
		paddingBottom: 18,
		boxShadow: 'var(--shadow-sm)',
		minHeight: 0,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 6,
		flexShrink: 0,
	},
	eyebrow: {
		fontFamily: 'var(--font-mono)',
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
	},
	eyebrowElevated: {
		fontSize: 11.5,
	},
	closeBtn: {
		width: 22,
		height: 22,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	body: {
		fontSize: 12.5,
		color: 'var(--ink)',
		lineHeight: 1.5,
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	time: {
		marginTop: 8,
		fontSize: 10,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
		flexShrink: 0,
	},
	resizeHandle: {
		boxSizing: 'border-box',
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: 6,
		cursor: 'row-resize',
		backgroundColor: 'transparent',
		borderBottomLeftRadius: 10,
		borderBottomRightRadius: 10,
		transition: 'background-color 120ms',
		':hover': {
			backgroundColor: 'var(--accent)',
			opacity: 0.4,
		},
	},
	resizeHandleDragging: {
		backgroundColor: 'var(--accent)',
		opacity: 0.6,
	},
});
